import { useEffect, useRef, useState } from 'react';
import { DRAW_COLORS } from '@avslack/shared';
import type { Track } from 'livekit-client';
import { useHuddle } from '../huddle/HuddleProvider';
import { Icon } from './Icon';

/**
 * A megosztott kepernyo - nagyitassal es rajzolassal.
 *
 * Harom reteg egymason:
 *  1. a video,
 *  2. a rajz-vaszon (mindenki latja),
 *  3. a vezerlok.
 *
 * A rajz pontjai 0-1 kozotti RELATIV koordinatak, nem pixelek. Enelkul egy
 * nagyobb kijelzon mashova kerulne a vonal, mint a rajzolonal - a rajz
 * ertelme pedig eppen az, hogy ugyanarra mutassunk.
 */
export function ScreenShareView({
  track,
  identity,
  label,
  isMine = false,
  frameNote = null,
  monitors = [],
  lastMonitor = null,
  onPickMonitor,
}: {
  track: Track;
  identity: string;
  label: string;
  /** A SAJAT megosztasunk - zold kerettel es "Te osztod meg" felirattal. */
  isMine?: boolean;
  /**
   * Miert nincs keret a KIJELZO szelen?
   *
   * `null` = van (vagy nem a mienk a megosztas). Ha nincs, MEGMONDJUK az
   * okat: egy nema hianyt a felhasznalo elrontott funkcionak lat.
   */
  frameNote?: string | null;
  /**
   * Valaszthato kijelzok - CSAK ha kerdeznunk kell.
   *
   * Ures lista = nincs mit kerdezni (mert egyertelmu volt, vagy mert
   * bongeszoben vagyunk).
   */
  monitors?: { name: string; x: number; y: number; primary: boolean }[];
  /**
   * A legutobb valasztott kijelzo - CSAK kiemelesre.
   *
   * Magatol sosem alkalmazzuk: a megosztott monitor megosztasonkent valtozhat,
   * es a tegnapi valasztas alapjan keretezni talalgatas lenne.
   */
  lastMonitor?: string | null;
  onPickMonitor?: (name: string) => void;
}) {
  const { strokes, sendStroke, clearStrokes } = useHuddle();

  const videoRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef<{ x: number; y: number }[] | null>(null);
  /** A HUZAS kezdopontja es a hozza tartozo eltolas. `null` = nem huzunk. */
  const panning = useRef<{ x: number; y: number; from: { x: number; y: number } } | null>(null);
  /** A teljes kepernyos nezet gazdaja - ezt kerjuk teljes kepernyore. */
  const shellRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [drawMode, setDrawMode] = useState(false);
  const [color, setColor] = useState<string>(DRAW_COLORS[0]);
  const [dragging, setDragging] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const mine = strokes.filter((stroke) => stroke.target === identity);

  // ─── Video ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const element = track.attach();
    element.className = 'block w-full h-full object-contain';
    videoRef.current?.replaceChildren(element);
    return () => element.remove();
  }, [track]);

  // ─── Rajz kirajzolasa ───────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    // A vaszon a megjelenitett merethez igazodik, a pontok relativok maradnak.
    canvas.width = parent.clientWidth;
    canvas.height = parent.clientHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;

    for (const stroke of mine) {
      if (stroke.points.length < 2) continue;
      ctx.strokeStyle = stroke.color;
      ctx.beginPath();
      stroke.points.forEach((point, index) => {
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  }, [mine]);

  // ─── Rajzolas ───────────────────────────────────────────────────────────────

  const toRelative = (event: React.PointerEvent): { x: number; y: number } => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  const onPointerDown = (event: React.PointerEvent) => {
    /*
      HUZAS nagyitasban. Eddig a kurzor "grab"-ot mutatott, de semmi nem
      fogadta a huzast - a felulet olyat igert, amit nem tudott. Csak a
      nyil-gombokkal lehetett mozogni.
    */
    if (!drawMode) {
      if (zoom <= 1) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      panning.current = { x: event.clientX, y: event.clientY, from: offset };
      setDragging(true);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = [toRelative(event)];
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (panning.current) {
      /*
        Kepernyo-pixelbol az eltolas szazalekaba.

        A transzformacio `scale(z) translate(tx%, ty%)`: a szazalek az elem
        SAJAT meretehez szol, es utana meg felnagyitja a `scale`. Egy `dx`
        pixeles elmozdulashoz tehat `dx / (W * z) * 100` szazalek kell -
        enelkul a kep a nagyitas mertekeben "elszaladna" az eger alol.
      */
      const box = event.currentTarget as HTMLElement;
      const width = box.offsetWidth || 1;
      const height = box.offsetHeight || 1;
      const dx = ((event.clientX - panning.current.x) / (width * zoom)) * 100;
      const dy = ((event.clientY - panning.current.y) / (height * zoom)) * 100;
      setOffset({
        x: clampOffset(panning.current.from.x + dx, zoom),
        y: clampOffset(panning.current.from.y + dy, zoom),
      });
      return;
    }
    if (!drawMode || !drawing.current) return;
    drawing.current.push(toRelative(event));

    /**
     * Darabokban kuldunk, nem vonalankent.
     *
     * Egy hosszu vonal vegen erkezo egyetlen csomag azt jelentene, hogy a
     * tobbiek csak utolag latjak, mit rajzolunk. 24 pontonkent kuldve a
     * vonal "elve" nő a masik oldalon is.
     */
    if (drawing.current.length % 24 === 0) {
      sendStroke({ target: identity, color, points: [...drawing.current.slice(-25)] });
    }
  };

  const onPointerUp = () => {
    if (panning.current) {
      panning.current = null;
      setDragging(false);
      return;
    }
    if (!drawing.current) return;
    if (drawing.current.length > 1) {
      sendStroke({ target: identity, color, points: drawing.current });
    }
    drawing.current = null;
  };

  // ─── Nagyitas ───────────────────────────────────────────────────────────────

  const adjustZoom = (delta: number) => {
    const next = Math.min(4, Math.max(1, Number((zoom + delta).toFixed(2))));
    setZoom(next);
    /*
      Kisebb nagyitasnal kevesebb eltolas fer bele - a `clampOffset` alapmereten
      (z=1) nullat ad, tehat a visszaallitas is elintezodik magatol.
    */
    setOffset((current) => ({
      x: clampOffset(current.x, next),
      y: clampOffset(current.y, next),
    }));
  };

  const pan = (delta: { x: number; y: number }) =>
    setOffset((current) => ({
      x: clampOffset(current.x + delta.x, zoom),
      y: clampOffset(current.y + delta.y, zoom),
    }));

  // ─── Teljes kepernyo ────────────────────────────────────────────────────────

  /*
    A BONGESZO teljes kepernyos modja, nem egy sajat "nagy" elrendezes.

    Igy tunik el a fejlec, a lablec es minden mas kromja - az egesz monitoron
    csak a megosztas latszik. A vezerlok a kepen BELUL allnak (abszolut
    pozicioval), ezert teljes kepernyon is ott maradnak: van mivel kilepni.
  */
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === shellRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = () => {
    const shell = shellRef.current;
    if (!shell) return;
    /*
      Az Esc-et a bongeszo maga kezeli, es a `fullscreenchange` visszaszol -
      ezert nem tartunk sajat "kilepes" billentyut. Ha a keres elbukik (a
      bongeszo megtagadja), a gomb egyszeruen nem csinal semmit: egy elszallt
      igeret rosszabb lenne, mint egy nem mukodo gomb.
    */
    if (document.fullscreenElement === shell) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void shell.requestFullscreen?.().catch(() => undefined);
    }
  };

  return (
    /*
      A SAJAT megosztas ZOLD KERETET kap.

      Megosztas kozben a legkonnyebb hiba a rossz ablakot valasztani, es ezt
      percekig eszre sem venni. A zold keret + a felirat egy pillantasbol
      megmondja, hogy amit itt latunk, azt latjak a tobbiek is.

      Ez a keret az ALKALMAZASON BELUL van. A KIJELZO szelere kulon keret
      kerul - azt az asztali app rajzolja (lasd `lib/share-frame.ts`), mert egy
      weboldal a sajat ablakan kivulre nem rajzolhat. Ahol az nem megy
      (bongeszo, vagy ket azonos felbontasu monitor), ott a `frameNote`
      megmondja, miert.
    */
    <div
      ref={shellRef}
      className={`relative flex-1 overflow-hidden bg-black${
        fullscreen ? '' : ' rounded-card'
      }${isMine ? ' ring-2 ring-[var(--share-live)] ring-offset-0' : ''}`}
    >
      <div
        /*
          HUZAS kozben NINCS atmenet: a `transition-transform` ilyenkor egy
          fel masodperces gumizast adna az eger utan, amitol a mozgatas
          pontatlannak erzodik. Gombnyomasra viszont jol all.
        */
        className={`h-full w-full${dragging ? '' : ' transition-transform duration-(--duration-quick)'}`}
        style={{
          transform: `scale(${zoom}) translate(${offset.x}%, ${offset.y}%)`,
          cursor: drawMode ? 'crosshair' : zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
          // Nagyitasban a bongeszo sajat "huzom a kepet" mozdulata zavarna.
          touchAction: zoom > 1 && !drawMode ? 'none' : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div ref={videoRef} className="h-full w-full" />
        <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
      </div>

      {/* ─── Vezerlok ──────────────────────────────────────────────────────── */}
      <div
        className={`absolute left-3 top-3 flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs text-white backdrop-blur ${
          isMine ? 'bg-[var(--share-live)]/90 font-medium' : 'bg-black/60'
        }`}
      >
        {isMine && <span aria-hidden className="size-1.5 rounded-full bg-white" />}
        <span className="max-w-48 truncate">
          {isMine ? 'Ezt osztod meg' : `${label} képernyője`}
        </span>
      </div>

      {/*
        MELYIK KIJELZO? - amikor magunktol nem tudjuk eldonteni.

        Harom egyforma, 1920x1080-as monitornal a rogzitett kep merete SOSEM
        dont, vagyis pont abban a felallasban nem, amiert az egesz funkcio
        keszult. Talalgatni viszont nem talalgatunk: a rossz kijelzore tett
        keret hamis biztonsagot adna arrol, hogy mit lat a masik fel.
        Ezert egyszer megkerdezzuk - a valasztast az app megjegyzi.
      */}
      {isMine && monitors.length > 0 && (
        <div className="absolute left-3 top-11 max-w-sm rounded-card bg-black/75 p-2.5 backdrop-blur">
          <p className="text-2xs text-white/80">
            Melyik kijelzőt osztod meg? A zöld keret arra kerül.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {monitors.map((monitor, index) => (
              <button
                key={monitor.name}
                type="button"
                onClick={() => onPickMonitor?.(monitor.name)}
                className={`rounded-control px-2.5 py-1 text-2xs font-medium text-white transition-colors ${
                  monitor.name === lastMonitor
                    ? 'bg-[var(--share-live)]/80 hover:bg-[var(--share-live)]'
                    : 'bg-white/15 hover:bg-white/25'
                }`}
              >
                {monitorLabel(index, monitors.length)}
                {monitor.primary && ' · fő'}
                {monitor.name === lastMonitor && ' · legutóbb'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Miert nincs keret magan a kijelzon - csak a megosztonak, csak ha kell. */}
      {isMine && !monitors.length && frameNote && (
        <p className="absolute left-3 top-11 max-w-xs rounded-card bg-black/60 px-2.5 py-1 text-2xs text-white/80 backdrop-blur">
          {frameNote}
        </p>
      )}

      <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-pill bg-black/60 p-1 backdrop-blur">
        <IconButton label="Kicsinyítés" onClick={() => adjustZoom(-0.25)} disabled={zoom <= 1}>
          −
        </IconButton>
        <span className="min-w-10 text-center text-2xs tabular-nums text-white/80">
          {Math.round(zoom * 100)}%
        </span>
        <IconButton label="Nagyítás" onClick={() => adjustZoom(0.25)} disabled={zoom >= 4}>
          +
        </IconButton>

        <span className="mx-0.5 h-4 w-px bg-white/20" />

        <IconButton
          label={fullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
          active={fullscreen}
          onClick={toggleFullscreen}
        >
          <Icon name="kepernyo" />
        </IconButton>

        <IconButton
          label={drawMode ? 'Rajzolás kikapcsolása' : 'Rajzolás'}
          active={drawMode}
          onClick={() => setDrawMode((current) => !current)}
        >
          <Icon name="szerkesztes" />
        </IconButton>

        {drawMode && (
          <>
            {DRAW_COLORS.map((option, index) => (
              <button
                key={option}
                type="button"
                aria-label={`${DRAW_COLOR_NAMES[index] ?? `${index + 1}.`} szín`}
                title={`${DRAW_COLOR_NAMES[index] ?? `${index + 1}.`} szín`}
                aria-pressed={color === option}
                onClick={() => setColor(option)}
                style={{ background: option }}
                className={`size-4 rounded-full transition-transform ${
                  color === option ? 'scale-125 ring-2 ring-white' : ''
                }`}
              />
            ))}
            <IconButton label="Rajz törlése" onClick={() => clearStrokes(identity)}>
              <Icon name="radir" />
            </IconButton>
          </>
        )}
      </div>

      {/* Nagyitasnal iranyitas - a csuszkanal egyszerubb es pontosabb. */}
      {zoom > 1 && !drawMode && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1 rounded-pill bg-black/60 p-1 backdrop-blur">
          {(
            [
              ['←', 'balra', { x: 6, y: 0 }],
              ['↑', 'fel', { x: 0, y: 6 }],
              ['↓', 'le', { x: 0, y: -6 }],
              ['→', 'jobbra', { x: -6, y: 0 }],
            ] as const
          ).map(([icon, direction, delta]) => (
            <IconButton key={icon} label={`Mozgatás ${direction}`} onClick={() => pan(delta)}>
              {icon}
            </IconButton>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A rajzszinek NEVE - a `DRAW_COLORS` sorrendjeben.
 *
 * A szinmintak csak egy-egy potty: kepernyoolvasoval mind "Szín választása"
 * volt, vagyis nem lehetett tudni, melyiket valasztod.
 */
const DRAW_COLOR_NAMES = ['Piros', 'Sárga', 'Zöld', 'Kék', 'Lila'];

function IconButton({
  label,
  onClick,
  active,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`grid size-7 place-items-center rounded-full text-sm transition-colors disabled:opacity-30 ${
        active ? 'bg-white text-black' : 'text-white hover:bg-white/20'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Emberi nev egy kijelzonek.
 *
 * A rendszer sajat neve (a Windows `DISPLAY2`-fele azonositoja) senkinek nem
 * mond semmit arrol, melyik monitorrol van szo. A kijelzok BALROL JOBBRA
 * erkeznek, tehat a
 * sorrendbol lehet ertheto nevet adni - ez az, amit a felhasznalo lat maga
 * elott.
 */
function monitorLabel(index: number, total: number): string {
  if (total === 1) return 'A kijelző';
  if (index === 0) return 'Bal szélső';
  if (index === total - 1) return 'Jobb szélső';
  return total === 3 ? 'Középső' : `${index + 1}. balról`;
}

/**
 * Meddig tolhato el a nagyitott kep.
 *
 * A transzformacio `scale(z) translate(tx%, ty%)`: a kepernyon latszo eltolas
 * `W * tx/100 * z`, a kiloghato resz pedig oldalanként `W * (z-1) / 2`. A ketto
 * egyenlosegebol jon a hatar. Enelkul a kepet (a nyil-gombokkal is) ki lehetett
 * tolni a lathato teruletrol, es csak talalgatva lehetett visszahozni.
 */
function clampOffset(value: number, zoom: number): number {
  if (zoom <= 1) return 0;
  const limit = (100 * (zoom - 1)) / (2 * zoom);
  return Math.min(limit, Math.max(-limit, Number(value.toFixed(2))));
}
